/**
 * CalibrationDisplay — cycle-102 sprint-1 Task 1.5.
 *
 * Per-skill calibration table for the AeonBriefSlot expansion: confidence,
 * base-rate, model id, freshness. Mounted as the expanded body of an
 * AeonBriefSlot so the operator can see the calibration of the same skill
 * whose narrative they are reading.
 *
 * Heuristic: `base_rate_correct` is the count of high-confidence briefs
 * (>= 0.7) over the lookback window. True outcome grading is deferred to
 * cycle-103 (documented in `backend/schemas/calibration_schemas.py`). The
 * panel surfaces the same caveat in a footer chip so the operator does
 * not over-trust the rate.
 */

import { useCalibrationSkills } from '../../../hooks/useIntel';
import { cn } from '../../../lib/utils';
import { isStale, type CalibrationSkill } from '../../../api/intelClient';

interface CalibrationDisplayProps {
  /** Optional filter to a single skill (defaults to all returned skills). */
  skillId?: string;
  topN?: number;
  className?: string;
}

export function CalibrationDisplay({
  skillId,
  topN = 10,
  className,
}: CalibrationDisplayProps) {
  const { data, isLoading, error, refetch } = useCalibrationSkills({ topN });

  // ── 503 / hard error per SDD §6.4 ──────────────────────────────
  if (error && !data) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    const aeonOffline = status === 503;
    return (
      <div
        role="alert"
        data-testid="calibration-fallback"
        className={cn(
          'flex flex-col gap-2 px-3 py-2 rounded-md border border-amber-500/40 bg-amber-500/5',
          className,
        )}
      >
        <span className="text-[10px] font-mono uppercase tracking-wider text-amber-500">
          {aeonOffline ? 'Calibration unavailable' : 'Calibration error'}
        </span>
        <p className="text-[11px] text-[var(--e-text-secondary)]">
          {aeonOffline
            ? 'Aeon offline'
            : error instanceof Error
              ? error.message
              : 'Unknown error'}
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="self-start px-2 py-1 text-[10px] font-mono uppercase tracking-wider rounded border border-[var(--e-border-secondary)] text-[var(--e-text-secondary)] hover:text-[var(--e-text-primary)] hover:bg-[var(--e-bg-elevated)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--e-purple-400)]"
        >
          Retry
        </button>
      </div>
    );
  }

  if (isLoading && !data) {
    return (
      <p
        role="status"
        aria-live="polite"
        className={cn(
          'px-3 py-2 text-[10px] font-mono text-[var(--e-text-muted)]',
          className,
        )}
      >
        Loading calibration…
      </p>
    );
  }

  if (!data) return null;

  const rows = skillId
    ? data.skills.filter((s) => s.skill_id === skillId)
    : data.skills;

  if (rows.length === 0) {
    return (
      <p
        role="status"
        className={cn('px-3 py-2 text-[10px] font-mono text-[var(--e-text-muted)]', className)}
      >
        No calibration data for this skill.
      </p>
    );
  }

  return (
    <div
      role="region"
      aria-label="Calibration display"
      className={cn(
        'flex flex-col gap-2 px-3 py-2 rounded-md border border-[var(--e-border-secondary)]/50 bg-[var(--e-bg-elevated)]/40',
        className,
      )}
    >
      <header className="flex items-center justify-between">
        <span className="text-[9px] font-mono uppercase tracking-widest text-[var(--e-text-muted)]">
          Calibration · last {data.lookback_days}d
        </span>
        <span className="text-[9px] font-mono text-[var(--e-text-muted)]/70">
          {rows.length} skill{rows.length === 1 ? '' : 's'}
        </span>
      </header>
      <table aria-label="Calibration metrics" className="w-full text-[10px] font-mono">
        <thead>
          <tr className="text-left text-[9px] uppercase tracking-wider text-[var(--e-text-muted)]">
            <th className="pb-1 font-medium">Skill</th>
            <th className="pb-1 font-medium text-right">Conf</th>
            <th className="pb-1 font-medium text-right">Base rate</th>
            <th className="pb-1 font-medium text-right">Fresh</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <CalibrationRow key={row.skill_id} row={row} />
          ))}
        </tbody>
      </table>
      <footer className="text-[9px] text-[var(--e-text-muted)]/70 leading-tight">
        Base-rate is a high-confidence (≥0.7) heuristic; true outcome grading
        ships in cycle-103.
      </footer>
    </div>
  );
}

function CalibrationRow({ row }: { row: CalibrationSkill }) {
  const stale = isStale(row.freshness_seconds);
  const fallback = row.error_state;

  return (
    <tr
      data-testid={`calibration-row-${row.skill_id}`}
      className="border-t border-[var(--e-border-secondary)]/30"
    >
      <td className="py-1 pr-2 text-[var(--e-text-secondary)]">
        <div className="leading-tight">{row.skill_id}</div>
        <div className="text-[var(--e-text-muted)] text-[9px]">{row.model_id}</div>
      </td>
      {fallback ? (
        <td colSpan={3} className="py-1 text-right text-amber-400">
          <span data-testid={`calibration-${row.skill_id}-fallback`}>
            {fallback.message}
          </span>
        </td>
      ) : (
        <>
          <td className="py-1 pr-2 text-right text-[var(--e-text-primary)]">
            {row.confidence.toFixed(2)}
          </td>
          <td className="py-1 pr-2 text-right text-[var(--e-text-secondary)]">
            {row.base_rate_correct} / {row.base_rate_total}
          </td>
          <td className="py-1 text-right">
            {stale ? (
              <span
                data-testid={`calibration-${row.skill_id}-stale`}
                className="text-amber-400"
              >
                {row.freshness_seconds}s
              </span>
            ) : (
              <span className="text-[var(--e-text-muted)]">{row.freshness_seconds}s</span>
            )}
          </td>
        </>
      )}
    </tr>
  );
}
